import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

export type ChipVariant = 'default' | 'primary' | 'success' | 'danger' | 'warning' | 'info';
export type ChipSeverity = 'success' | 'danger' | 'warning' | 'info' | 'secondary';
export type ChipSize = 'sm' | 'md' | 'lg' | 'small';

/**
 * GAP-FE-021B · migrated from decorator @Input/@Output to signal input()/output().
 *
 * Legacy aliases (severity → variant, 'small' size → 'sm') flow through
 * computed effectiveVariant/effectiveSize signals.
 */
@Component({
  selector: 'ui-chip',
  standalone: true,
  imports: [],
  templateUrl: './chip.component.html',
  styleUrl: './chip.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChipComponent {
  readonly label = input<string | undefined>(undefined);
  readonly variant = input<ChipVariant>('default');
  readonly severity = input<ChipSeverity | undefined>(undefined);
  readonly size = input<ChipSize>('md');
  readonly icon = input<string | undefined>(undefined);
  readonly removable = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly clickable = input<boolean>(false);

  readonly removed = output<void>();
  readonly chipClick = output<void>();

  /** Map severity → variant when severity is set, otherwise use variant directly. */
  readonly effectiveVariant = computed<ChipVariant>(() => {
    const sev = this.severity();
    if (sev !== undefined) {
      const severityMap: Record<ChipSeverity, ChipVariant> = {
        success: 'success',
        danger: 'danger',
        warning: 'warning',
        info: 'info',
        secondary: 'default'
      };
      return severityMap[sev] ?? 'default';
    }
    return this.variant();
  });

  /** Normalize legacy 'small' size to 'sm'. */
  readonly effectiveSize = computed<ChipSize>(() => {
    const s = this.size();
    return s === 'small' ? 'sm' : s;
  });

  readonly chipClasses = computed<string>(() => {
    const sizeClasses: Record<string, string[]> = {
      sm: ['px-2', 'py-0.5', 'text-xs', 'gap-1'],
      md: ['px-2.5', 'py-1', 'text-xs', 'gap-1.5'],
      lg: ['px-3', 'py-1.5', 'text-sm', 'gap-2']
    };

    const variantClasses: Record<ChipVariant, string[]> = {
      default: ['bg-bb-bg-tertiary', 'text-bb-text-primary', 'border-bb-border'],
      primary: ['bg-bb-accent-blue/10', 'text-bb-accent-blue', 'border-bb-accent-blue/20'],
      success: ['bg-bb-accent-green/10', 'text-bb-accent-green', 'border-bb-accent-green/20'],
      danger: ['bg-bb-accent-red/10', 'text-bb-accent-red', 'border-bb-accent-red/20'],
      warning: ['bg-bb-accent-amber/10', 'text-bb-accent-amber', 'border-bb-accent-amber/20'],
      info: ['bg-bb-accent-blue/10', 'text-bb-accent-blue', 'border-bb-accent-blue/20']
    };

    const baseClasses = [
      'inline-flex',
      'items-center',
      'rounded-full',
      'font-medium',
      'border',
      'transition-all',
      'duration-200',
      'max-w-full',
      ...sizeClasses[this.effectiveSize()],
      ...variantClasses[this.effectiveVariant()]
    ];

    if (this.disabled()) {
      baseClasses.push('opacity-50', 'cursor-not-allowed');
    } else if (this.clickable()) {
      baseClasses.push('cursor-pointer', 'hover:opacity-80', 'active:scale-95');
    }

    return baseClasses.join(' ');
  });

  onRemove(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.disabled()) {
      this.removed.emit();
    }
  }

  onClick(): void {
    if (!this.disabled() && this.clickable()) {
      this.chipClick.emit();
    }
  }
}
